/*
  assets/frontend-live-qa-diagnostic.js — FRONTEND-LIVE-QA-DIAGNOSTIC-05
  Updated: 2026-09-11

  Owner: Opt-in frontend live QA diagnostics for LittleG /  GTender surfaces.
  Inspected Auth Authority: assets/gcity-auth.js only.

  Activation:
  - ?gcity_debug=1, or localStorage.gcity_debug=1
  - ?gcity_debug=0 disables the diagnostic and restores patched browser APIs

  Note:
  - Read-only diagnostics and same-origin GET probes only.
  - No customer, GFun, checkout, order, tender or external telemetry writes.
  - Network observation is installed only while diagnostics are enabled.
  - Retired Download Center owners are not probed or represented as live surfaces.
  - Retired role-router fallbacks are never read, observed or treated as auth authority.
*/
(function () {
  'use strict';

  /* ==========================================================================
     SECTION 1: Core State & Version Control
     ========================================================================== */
  var VERSION = 'FRONTEND-LIVE-QA-DIAGNOSTIC-05';
  var STORAGE_KEY = 'gcity_debug';
  var PANEL_ID = 'gcity-live-qa-diagnostic';
  var STYLE_ID = 'gcity-live-qa-style';
  var REQUEST_LIMIT = 80;
  var FORBIDDEN_LIMIT = 40;

  var observedRequests = [];
  var forbiddenEvents = [];
  var renderTimer = null;
  var lifecycleController = null;
  var observerInstalled = false;

  var originals = {
    fetch: window.fetch,
    xhrOpen: window.XMLHttpRequest && window.XMLHttpRequest.prototype
      ? window.XMLHttpRequest.prototype.open
      : null,
    xhrSend: window.XMLHttpRequest && window.XMLHttpRequest.prototype
      ? window.XMLHttpRequest.prototype.send
      : null
  };

  var patched = {
    fetch: null,
    xhrOpen: null,
    xhrSend: null
  };

  var xhrMetadata = typeof WeakMap === 'function' ? new WeakMap() : null;


  /* ==========================================================================
     SECTION 2: Safe Utility Functions & Fallbacks
     ========================================================================== */
  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function escapeHTML(value) {
    return clean(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function safeOrigin() {
    return window.location.origin
      || (window.location.protocol + '//' + window.location.host);
  }

  function queryParams() {
    try {
      if (typeof URLSearchParams === 'function') {
        return new URLSearchParams(window.location.search || '');
      }
    } catch (_error) {}

    var search = window.location.search || '';
    return {
      get: function (key) {
        var match = new RegExp('[?&]' + key + '=([^&]*)').exec(search);
        return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
      }
    };
  }

  function now() {
    return new Date().toISOString();
  }

  function readStorage() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch (_error) {
      return false;
    }
  }

  function writeStorage(enabled) {
    try {
      if (enabled) window.localStorage.setItem(STORAGE_KEY, '1');
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch (_error) {}
  }

  function requestedState() {
    var value = clean(queryParams().get('gcity_debug')).toLowerCase();
    if (value === '0' || value === 'false' || value === 'off') {
      writeStorage(false);
      return false;
    }
    if (value === '1' || value === 'true' || value === 'on') {
      writeStorage(true);
      return true;
    }
    return readStorage();
  }

  function attr(element, name) {
    return element && element.getAttribute
      ? clean(element.getAttribute(name))
      : '';
  }


  /* ==========================================================================
     SECTION 3: Request Inspection & Forbidden Write Detection
     ========================================================================== */
  function methodFrom(input, init) {
    if (init && init.method) return clean(init.method).toUpperCase();
    if (input && input.method) return clean(input.method).toUpperCase();
    return 'GET';
  }

  function urlFrom(input) {
    try {
      var origin = safeOrigin();
      if (typeof input === 'string') {
        return new URL(input, origin).toString();
      }
      if (input && input.url) {
        return new URL(input.url, origin).toString();
      }
    } catch (_error) {}
    return clean((input && input.url) || input);
  }

  function pathFrom(url) {
    try {
      return new URL(url, safeOrigin()).pathname;
    } catch (_error) {
      return clean(url);
    }
  }

  function isWriteMethod(method) {
    return ['POST', 'PUT', 'PATCH', 'DELETE'].indexOf(
      clean(method).toUpperCase()
    ) !== -1;
  }

  function forbiddenReason(method, url) {
    if (!isWriteMethod(method)) return '';

    var path = pathFrom(url).toLowerCase();
    if (/\/admin(?:\/|$)/.test(path)) return 'shopify_admin_write';
    if (/\/apps\/gcity(?:-api)?\/gfun\/(deduct|grant|consume|topup|refund|ledger)/.test(path)) {
      return 'gfun_ledger_write';
    }
    if (/\/apps\/gcity(?:-api)?\/account\/(callback|login|logout|session)/.test(path)) {
      return 'auth_session_mutation';
    }
    if (/\/apps\/gcity(?:-api)?\/.*(customer|metafield|verification|promote)/.test(path)) {
      return 'customer_or_verification_write';
    }
    if (/\/(checkout|orders?)(?:\/|$)/.test(path)) return 'checkout_or_order_mutation';
    if (/\/apps\/gcity(?:-api)?\/gtender\/.*(submit|submission|publish|approve|award)/.test(path)) {
      return 'formal_tender_write';
    }
    return '';
  }

  function trimQueue(queue, limit) {
    while (queue.length > limit) queue.shift();
  }

  function recordRequest(method, url, status, reason) {
    var item = {
      time: now(),
      method: clean(method).toUpperCase() || 'GET',
      url: clean(url),
      path: pathFrom(url),
      status: status == null ? '' : status,
      forbidden_reason: clean(reason)
    };

    observedRequests.push(item);
    trimQueue(observedRequests, REQUEST_LIMIT);

    if (reason) {
      forbiddenEvents.push(item);
      trimQueue(forbiddenEvents, FORBIDDEN_LIMIT);
      document.documentElement.setAttribute(
        'data-gcity-live-qa-forbidden-write',
        'true'
      );
      renderSoon();
    }

    return item;
  }


  /* ==========================================================================
     SECTION 4: Fetch & XHR Interceptor Pipeline
     ========================================================================== */
  function installNetworkObserver() {
    if (observerInstalled) return;
    observerInstalled = true;

    if (typeof originals.fetch === 'function') {
      patched.fetch = function (input, init) {
        var method = methodFrom(input, init);
        var url = urlFrom(input);
        var reason = forbiddenReason(method, url);

        return originals.fetch.apply(this, arguments)
          .then(function (response) {
            recordRequest(method, url, response && response.status, reason);
            return response;
          })
          .catch(function (error) {
            recordRequest(method, url, 'error', reason);
            throw error;
          });
      };
      window.fetch = patched.fetch;
    }

    if (
      window.XMLHttpRequest
      && window.XMLHttpRequest.prototype
      && typeof originals.xhrOpen === 'function'
      && typeof originals.xhrSend === 'function'
    ) {
      patched.xhrOpen = function (method, url) {
        var metadata = {
          method: clean(method).toUpperCase() || 'GET',
          url: urlFrom(url),
          observed: false
        };
        if (xhrMetadata) xhrMetadata.set(this, metadata);
        else this.__gcityLiveQaMetadata = metadata;
        return originals.xhrOpen.apply(this, arguments);
      };

      patched.xhrSend = function () {
        var xhr = this;
        var metadata = xhrMetadata
          ? xhrMetadata.get(xhr)
          : xhr.__gcityLiveQaMetadata;

        if (metadata && !metadata.observed) {
          metadata.observed = true;
          xhr.addEventListener('loadend', function () {
            recordRequest(
              metadata.method,
              metadata.url,
              xhr.status || 'error',
              forbiddenReason(metadata.method, metadata.url)
            );
          }, { once: true });
        }

        return originals.xhrSend.apply(xhr, arguments);
      };

      window.XMLHttpRequest.prototype.open = patched.xhrOpen;
      window.XMLHttpRequest.prototype.send = patched.xhrSend;
    }

    window.__GCITY_LIVE_QA_NETWORK_OBSERVER__ = VERSION;
  }

  function restoreNetworkObserver() {
    if (!observerInstalled) return;

    if (patched.fetch && window.fetch === patched.fetch) {
      window.fetch = originals.fetch;
    }

    if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
      if (patched.xhrOpen && window.XMLHttpRequest.prototype.open === patched.xhrOpen) {
        window.XMLHttpRequest.prototype.open = originals.xhrOpen;
      }
      if (patched.xhrSend && window.XMLHttpRequest.prototype.send === patched.xhrSend) {
        window.XMLHttpRequest.prototype.send = originals.xhrSend;
      }
    }

    patched.fetch = null;
    patched.xhrOpen = null;
    patched.xhrSend = null;
    observerInstalled = false;
    delete window.__GCITY_LIVE_QA_NETWORK_OBSERVER__;
  }


  /* ==========================================================================
     SECTION 5: Diagnostic State Inspection
     ========================================================================== */
  function statusClass(value) {
    var normalized = clean(value).toLowerCase();
    if (['pass', 'ready', 'live', 'true', 'ok'].indexOf(normalized) !== -1) return 'pass';
    if (['fail', 'error', 'false', 'missing'].indexOf(normalized) !== -1) return 'fail';
    return 'warn';
  }

  function row(label, value) {
    var rendered = value == null ? '' : value;
    return '<div class="gcity-live-qa__row"><span>'
      + escapeHTML(label)
      + '</span><strong class="is-'
      + statusClass(rendered)
      + '">'
      + escapeHTML(rendered)
      + '</strong></div>';
  }

  function portalRoots() {
    return Array.prototype.slice.call(document.querySelectorAll(
      '[data-client-portal], [data-gtender-partner-dashboard], [data-portal-account-links]'
    )).map(function (element) {
      return {
        tag: element.tagName.toLowerCase(),
        role: attr(element, 'data-portal-role')
          || attr(element, 'data-required-role')
          || attr(element, 'data-required-roles'),
        authenticated: attr(element, 'data-portal-authenticated')
          || attr(element, 'data-client-portal-authenticated')
          || attr(element, 'data-gpd-authenticated'),
        runtime: attr(element, 'data-client-portal-runtime')
          || attr(element, 'data-gpd-runtime'),
        access: attr(element, 'data-client-portal-access')
          || attr(element, 'data-gpd-access')
      };
    });
  }

  function gtenderState() {
    var root = document.querySelector('[data-gtender-marketplace]')
      || document.querySelector('[data-module="gtender-app"]');
    var registry = window.GTENDER || {};

    return {
      present: Boolean(root),
      bridge_mode: (root && attr(root, 'data-gtender-bridge-mode'))
        || (registry.runtime && registry.runtime.liveBridgeMode)
        || 'n/a',
      live_loaded: root ? attr(root, 'data-gtender-live-loaded') : 'n/a',
      static_loaded: root ? attr(root, 'data-gtender-static-loaded') : 'n/a',
      registry_count: root ? attr(root, 'data-gtender-registry-count') : '',
      signal_count: root ? attr(root, 'data-gtender-signal-count') : '',
      live_error: root ? attr(root, 'data-gtender-live-error') : ''
    };
  }

  function portalState() {
    var auth = window.GCityAuth;
    var current = auth && typeof auth.current === 'function'
      ? auth.current()
      : null;
    var html = document.documentElement;
    var authenticated = current
      ? current.authenticated === true
      : attr(html, 'data-identity-state') === 'authenticated';

    return {
      gcity_auth: Boolean(auth),
      gcity_auth_version: (auth && (auth.VERSION || auth.version)) || '',
      identity_authority: (current && current.identity_authority)
        || (auth && auth.identityAuthority)
        || attr(html, 'data-identity-authority'),
      identity_state: attr(html, 'data-identity-state')
        || (authenticated ? 'authenticated' : 'guest'),
      platform_account_owner: (current && current.platform_account_owner)
        || (auth && auth.platformAccountOwner)
        || attr(html, 'data-platform-account-owner'),
      platform_account_state: (current && current.platform_account_state)
        || attr(html, 'data-platform-account-state'),
      platform_account_link_state: (current && current.platform_account_link_state)
        || attr(html, 'data-platform-account-link-state'),
      authorization_Note: (current && current.authorization_Note)
        || (auth && auth.authorizationNote)
        || attr(html, 'data-authorization-Note'),
      role_router: Boolean(auth),
      router_version: (auth && (auth.VERSION || auth.version)) || '',
      authenticated: String(authenticated),
      role: (current && current.role)
        || attr(html, 'data-portal-role'),
      route: (current && current.route)
        || attr(html, 'data-portal-route')
    };
  }

  function pageState() {
    var body = document.body || {};
    return {
      url: window.location.pathname + window.location.search,
      template: attr(body, 'data-template'),
      page_handle: attr(body, 'data-page-handle'),
      customer_liquid_logged_in: Boolean(
        window.CUSTOMER_DATA && window.CUSTOMER_DATA.isLoggedIn === true
      ),
      gcity_ready: attr(body, 'data-gcity-ready')
        || attr(body, 'data-gcityReady')
    };
  }

  function getSnapshot() {
    return {
      version: VERSION,
      time: now(),
      page: pageState(),
      portal: portalState(),
      portal_roots: portalRoots(),
      gtender: gtenderState(),
      forbidden_write_observed: forbiddenEvents.length > 0,
      forbidden_events: forbiddenEvents.slice(),
      recent_requests: observedRequests.slice(-12)
    };
  }


  /* ==========================================================================
     SECTION 6: UI Panel & Stylesheet Injection
     ========================================================================== */
  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      '.gcity-live-qa{position:fixed;right:14px;bottom:14px;z-index:2147483646;width:min(420px,calc(100vw - 28px));max-height:min(78vh,720px);overflow:auto;border:1px solid rgba(255,255,255,.16);border-radius:18px;background:rgba(17,17,17,.94);color:#fff;box-shadow:0 24px 80px rgba(0,0,0,.38);font:12px/1.35 ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace;-webkit-backdrop-filter:blur(18px);backdrop-filter:blur(18px)}',
      '.gcity-live-qa[hidden]{display:none!important}',
      '.gcity-live-qa__head{position:sticky;top:0;display:flex;align-items:center;justify-content:space-between;gap:8px;padding:12px 12px 10px;background:rgba(17,17,17,.98);border-bottom:1px solid rgba(255,255,255,.12);z-index:1}',
      '.gcity-live-qa__head strong{font-size:12px}',
      '.gcity-live-qa__actions{display:flex;gap:6px;flex-wrap:wrap}',
      '.gcity-live-qa button{border:1px solid rgba(255,255,255,.18);border-radius:999px;background:rgba(255,255,255,.08);color:#fff;padding:6px 9px;font:inherit;cursor:pointer}',
      '.gcity-live-qa button:hover{background:rgba(255,255,255,.16)}',
      '.gcity-live-qa__body{padding:10px 12px 12px}',
      '.gcity-live-qa__section{border:1px solid rgba(255,255,255,.10);border-radius:14px;padding:9px;margin:0 0 8px;background:rgba(255,255,255,.04)}',
      '.gcity-live-qa__section h3{margin:0 0 7px;font-size:11px;color:rgba(255,255,255,.62);letter-spacing:.08em;text-transform:uppercase}',
      '.gcity-live-qa__row{display:grid;grid-template-columns:minmax(0,1fr) minmax(96px,1fr);gap:8px;margin:4px 0}',
      '.gcity-live-qa__row span{color:rgba(255,255,255,.58);overflow:hidden;text-overflow:ellipsis}',
      '.gcity-live-qa__row strong{font-weight:700;overflow:hidden;text-overflow:ellipsis}',
      '.gcity-live-qa .is-pass{color:#86efac}',
      '.gcity-live-qa .is-warn{color:#fde68a}',
      '.gcity-live-qa .is-fail{color:#fca5a5}',
      '.gcity-live-qa__pre{max-height:180px;overflow:auto;white-space:pre-wrap;overflow-wrap:anywhere;border-radius:12px;background:rgba(0,0,0,.28);padding:8px;color:rgba(255,255,255,.78)}',
      '@media(max-width:640px){.gcity-live-qa{right:10px;bottom:10px;width:calc(100vw - 20px);max-height:72vh}}'
    ].join('');
    document.head.appendChild(style);
  }

  function panelMarkup(snapshot) {
    return '<div class="gcity-live-qa__head"><strong>GCity Live QA · '
      + escapeHTML(snapshot.version)
      + '</strong><div class="gcity-live-qa__actions">'
      + '<button type="button" data-gcity-live-qa-refresh>Refresh</button>'
      + '<button type="button" data-gcity-live-qa-copy>Copy JSON</button>'
      + '<button type="button" data-gcity-live-qa-close>Close</button>'
      + '</div></div><div class="gcity-live-qa__body">'
      + '<section class="gcity-live-qa__section"><h3>Page</h3>'
      + row('url', snapshot.page.url)
      + row('template', snapshot.page.template || 'n/a')
      + row('page_handle', snapshot.page.page_handle || 'n/a')
      + row('liquid customer', String(snapshot.page.customer_liquid_logged_in))
      + '</section>'
      + '<section class="gcity-live-qa__section"><h3>Identity / Account Note</h3>'
      + row('GCityAuth', String(snapshot.portal.gcity_auth))
      + row('identity authority', snapshot.portal.identity_authority || 'n/a')
      + row('identity state', snapshot.portal.identity_state || 'n/a')
      + row('platform account owner', snapshot.portal.platform_account_owner || 'n/a')
      + row('platform account state', snapshot.portal.platform_account_state || 'n/a')
      + row('account link state', snapshot.portal.platform_account_link_state || 'n/a')
      + row('authorization Note', snapshot.portal.authorization_Note || 'n/a')
      + row('Role router', String(snapshot.portal.role_router))
      + row('authenticated', snapshot.portal.authenticated || 'n/a')
      + row('presentation role', snapshot.portal.role || 'n/a')
      + row('route', snapshot.portal.route || 'n/a')
      + '</section>'
      + '<section class="gcity-live-qa__section"><h3>GTender Bridge</h3>'
      + row('present', String(snapshot.gtender.present))
      + row('bridge mode', snapshot.gtender.bridge_mode)
      + row('live loaded', snapshot.gtender.live_loaded)
      + row('static loaded', snapshot.gtender.static_loaded)
      + row('registry count', snapshot.gtender.registry_count || 'n/a')
      + row('signal count', snapshot.gtender.signal_count || 'n/a')
      + row('live error', snapshot.gtender.live_error || 'none')
      + '</section>'
      + '<section class="gcity-live-qa__section"><h3>Forbidden Writes</h3>'
      + row('observed', String(snapshot.forbidden_write_observed))
      + row('count', String(snapshot.forbidden_events.length))
      + '<div class="gcity-live-qa__pre">'
      + escapeHTML(JSON.stringify(snapshot.forbidden_events.slice(-5), null, 2))
      + '</div></section>'
      + '<section class="gcity-live-qa__section"><h3>Raw Snapshot</h3>'
      + '<div class="gcity-live-qa__pre">'
      + escapeHTML(JSON.stringify(snapshot, null, 2))
      + '</div></section></div>';
  }

  function ensurePanel() {
    var panel = document.getElementById(PANEL_ID);
    if (panel) return panel;

    panel = document.createElement('aside');
    panel.id = PANEL_ID;
    panel.className = 'gcity-live-qa';
    panel.setAttribute('aria-label', 'GCity live QA diagnostics');
    panel.setAttribute('aria-live', 'polite');
    panel.addEventListener('click', function (event) {
      var target = event.target && event.target.closest
        ? event.target.closest('button')
        : null;
      if (!target || !panel.contains(target)) return;

      if (target.hasAttribute('data-gcity-live-qa-refresh')) render();
      else if (target.hasAttribute('data-gcity-live-qa-copy')) copySnapshot();
      else if (target.hasAttribute('data-gcity-live-qa-close')) disable();
    });
    document.body.appendChild(panel);
    return panel;
  }

  function render() {
    if (!requestedState() || !document.body) return;
    injectStyle();
    var panel = ensurePanel();
    panel.hidden = false;
    panel.innerHTML = panelMarkup(getSnapshot());
  }

  function renderSoon() {
    if (!requestedState()) return;
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(render, 180);
  }

  function copySnapshot() {
    var text = JSON.stringify(getSnapshot(), null, 2);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(function () {
        window.prompt('Copy GCity QA snapshot', text);
      });
      return;
    }
    window.prompt('Copy GCity QA snapshot', text);
  }


  /* ==========================================================================
     SECTION 7: Endpoint Probing & Lifecycle Controls
     ========================================================================== */
  function probeEndpoint(path) {
    if (typeof originals.fetch !== 'function') {
      return Promise.resolve({ path: path, status: 'fetch-unavailable', ok: false });
    }

    return originals.fetch(path, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (response) {
      recordRequest('GET', path, response.status, '');
      return { path: path, status: response.status, ok: response.ok };
    }).catch(function () {
      recordRequest('GET', path, 'error', '');
      return { path: path, status: 'error', ok: false };
    });
  }

  function runProbes() {
    if (!requestedState()) return Promise.resolve([]);

    var paths = [
      '/apps/gcity/account/session',
      '/apps/gcity/account/me'
    ];

    if (
      document.querySelector('[data-gtender-marketplace]')
      || window.location.pathname.indexOf('/pages/gtender') === 0
    ) {
      paths.push(
        '/apps/gcity/gtender/registry',
        '/apps/gcity/gtender/signals'
      );
    }

    return Promise.all(paths.map(probeEndpoint)).then(function (results) {
      window.__GCITY_LIVE_QA_PROBES__ = results;
      renderSoon();
      return results;
    });
  }

  function bindLifecycle() {
    if (lifecycleController) return;
    lifecycleController = typeof AbortController === 'function'
      ? new AbortController()
      : null;
    var options = lifecycleController
      ? { signal: lifecycleController.signal }
      : undefined;

    var registrations = [
      [window, 'gcity:auth-entries:ready', renderSoon],
      [document, 'gcity:auth:ready', renderSoon],
      [document, 'gtender:static-data-ready', renderSoon],
      [document, 'gtender:live-bridge-ready', renderSoon],
      [window, 'littleg:city-mission:result-saved', renderSoon]
    ];

    registrations.forEach(function (registration) {
      registration[0].addEventListener(
        registration[1],
        registration[2],
        options
      );
    });
  }

  function unbindLifecycle() {
    if (lifecycleController) {
      lifecycleController.abort();
      lifecycleController = null;
    }
  }


  /* ==========================================================================
     SECTION 8: Module Activation & Global Export
     ========================================================================== */
  function enable() {
    writeStorage(true);
    installNetworkObserver();
    bindLifecycle();
    document.documentElement.setAttribute(
      'data-gcity-live-qa-diagnostic',
      'enabled'
    );

    function start() {
      render();
      runProbes();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }

    return true;
  }

  function disable() {
    writeStorage(false);
    restoreNetworkObserver();
    unbindLifecycle();
    window.clearTimeout(renderTimer);
    renderTimer = null;

    var panel = document.getElementById(PANEL_ID);
    if (panel) panel.remove();
    var style = document.getElementById(STYLE_ID);
    if (style) style.remove();

    document.documentElement.removeAttribute('data-gcity-live-qa-diagnostic');
    document.documentElement.removeAttribute('data-gcity-live-qa-forbidden-write');
    return true;
  }

  window.GCityFrontendQADiagnostic = Object.freeze({
    VERSION: VERSION,
    enabled: requestedState,
    enable: enable,
    disable: disable,
    snapshot: getSnapshot,
    render: render,
    probe: runProbes,
    forbiddenEvents: function () { return forbiddenEvents.slice(); },
    recentRequests: function () { return observedRequests.slice(); }
  });

  if (requestedState()) enable();
  else restoreNetworkObserver();
})();